import {
  List,
  Datagrid,
  TextField,
  FunctionField,
  TextInput,
  SelectInput,
  EditButton,
  RaRecord,
} from 'react-admin';
import { Chip, Typography } from '@mui/material';
import { ROLES, roleLabel } from './role';
import { InlinePair, compactDatagridSx } from '../../shared/listCells';
import { menuLabel } from '../menuConfig';
import { ClientDayField } from '../../shared/DateFields';
import { useCustomRoles } from '../roleCenter/customRoles';

/**
 * Filtry listy. Role własne z centrum ról (22.10.2026, `roleCenter`) dochodzą
 * do wyboru roli — bez centrum lista ról jak dotąd.
 */
function useFilters() {
  const custom = useCustomRoles();
  return [
    <TextInput source="q" label="Szukaj (nazwa / adres)" alwaysOn />,
    <SelectInput
      source="role"
      label="Rola"
      choices={[...ROLES, ...custom.map((r) => ({ id: r.id, name: r.name }))]}
    />,
  ];
}

/**
 * Imię i nazwisko konta (07.10.2026) albo `null`, gdy żadne nie jest
 * wpisane — wtedy lista pokazuje login, nie zgadujemy z niego nazwiska.
 */
export function personName(record: Record<string, unknown> | undefined): string | null {
  const parts = [record?.firstName, record?.lastName]
    .filter((p): p is string => typeof p === 'string' && p.trim() !== '')
    .map((p) => p.trim());
  return parts.length > 0 ? parts.join(' ') : null;
}

/**
 * Konto z adresem obok — po adresie loguje się użytkownik. Z imieniem
 * i nazwiskiem (07.10.2026) na pierwszym miejscu stoi osoba, a login
 * przechodzi obok adresu.
 */
export const AccountCell = ({ record }: { record: RaRecord & Record<string, unknown> }) => {
  const name = personName(record);
  const username = String(record.username ?? '—');
  const address = String(record.email ?? '—');
  return (
    <InlinePair
      primary={name ?? username}
      secondary={name ? `${username} · ${address}` : address}
      maxWidth={{ xs: 360, xl: 480 }}
    />
  );
};

/**
 * Rola z wyróżnieniem kont podwyższonych.
 *
 * Administrator i konto maszynowe mają rzucać się w oczy na liście — to one
 * niosą ryzyko, a nie zwykły użytkownik.
 */
const RoleChip = ({ record }: { record: RaRecord & Record<string, unknown> }) => {
  const custom = useCustomRoles();
  const id = String(record.role ?? '');
  const elevated = id === 'ADMIN' || id === 'COMPANY_ADMIN';
  const machine = id === 'API_CLIENT';
  return (
    <Chip
      size="small"
      variant={elevated ? 'filled' : 'outlined'}
      color={elevated ? 'warning' : machine ? 'info' : 'default'}
      label={roleLabel(id, custom)}
    />
  );
};

const CompanyCell = ({ record }: { record: RaRecord & Record<string, unknown> }) => {
  const f = record.company as Record<string, unknown> | undefined;
  if (!f) {
    return (
      <Typography variant="caption" color="text.secondary">
        bez spółki
      </Typography>
    );
  }
  return <span>{String(f.country ?? f.name)}</span>;
};

/**
 * Konta użytkowników.
 *
 * Widoczne wyłącznie dla ról z uprawnieniem do zarządzania kontami —
 * o dostępie rozstrzyga API, panel tylko nie pokazuje pozycji w menu.
 */
export const UserList = () => (
  <List filters={useFilters()} perPage={25} title={menuLabel('users')}>
    <Datagrid rowClick="edit" bulkActionButtons={false} sx={compactDatagridSx}>
      <FunctionField label="Konto" render={(r: RaRecord) => <AccountCell record={r} />} />
      <FunctionField label="Rola" render={(r: RaRecord) => <RoleChip record={r} />} />
      <FunctionField label="Spółka" render={(r: RaRecord) => <CompanyCell record={r} />} />
      <TextField source="phone" label="Telefon" />
      <ClientDayField source="createdAt" label="Utworzono" />
      <EditButton label="Zmień" />
    </Datagrid>
  </List>
);
